import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language || 'en');

  // Initialize language from storage once on mount
  useEffect(() => {
    const storedLanguage = localStorage.getItem('preferredLanguage') ||
                          sessionStorage.getItem('currentLanguage') ||
                          i18n.language ||
                          'en';
    const code = String(storedLanguage).split('-')[0];
    setCurrentLanguage(code);
    if (i18n.language !== code) {
      i18n.changeLanguage(code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- init once
  }, []);

  // Keep <html lang> and dir in sync for accessibility / SEO / RTL
  useEffect(() => {
    const code = String(currentLanguage || 'en').split('-')[0];
    if (typeof document !== 'undefined') {
      document.documentElement.lang = code;
      document.documentElement.dir = code === 'ar' ? 'rtl' : 'ltr';
    }
  }, [currentLanguage]);

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChange = (lng) => {
      setCurrentLanguage(lng);
      localStorage.setItem('preferredLanguage', lng);
      sessionStorage.setItem('currentLanguage', lng);
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  const changeLanguage = async (languageCode) => {
    try {
      // Change the language directly
      await i18n.changeLanguage(languageCode);
      
      // Update local state
      setCurrentLanguage(languageCode);
      
      // Store in both storages for persistence
      localStorage.setItem('preferredLanguage', languageCode);
      sessionStorage.setItem('currentLanguage', languageCode);
      
      return true;
    } catch (error) {
      return false;
    }
  };

  const lang = (currentLanguage || 'en').split('-')[0];
  const value = {
    currentLanguage,
    changeLanguage,
    isEnglish: lang === 'en',
    isFrench: lang === 'fr',
    isDutch: lang === 'nl',
    isArabic: lang === 'ar',
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
